import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDE from '@angular/common/locales/de';
import localeEN from '@angular/common/locales/en';
import { enableProdMode, inject, provideAppInitializer } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
// Set moment.js global locale to 'de' before Angular bootstraps,
// so pas-datetime-picker shows DD.MM.YYYY format
import moment from 'moment';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import { Observable, of } from 'rxjs';

import { environment } from '../environments/environment';

import { ApiInterceptor } from './app/api-interceptor';
import { AppComponent } from './app/app.component';
import { appInitializerFactory, initializeApiConfigFactory } from './app/app.initialize-functions';
import { AppInitializers } from './app/app.initializers';
import { APP_ROUTES } from './app/app.routing';
import { AuthInterceptor } from './app/auth-interceptor';
import { provideEnvironmentConfig } from './app/core/environment-config/environment-config.provider';
import { determineConfigEnvironment, getHostname, RcEnvironment } from './app/env.config';
import { ContextService } from './app/services/context/context.service';
import { ContextServiceImpl } from './app/services/context/context.service.impl';
import { ConsoleLoggerService } from './app/services/logging-service/console-logger.service';
import { LoggerService } from './app/services/logging-service/logger.service';
import { LoginService } from './app/services/user-service/login.service';
import deTranslations from './assets/i18n/de.json';
import enTranslations from './assets/i18n/en.json';

import 'moment/locale/de';

// ag-grid v33+ requires explicit module registration and legacy theme opt-in
ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({ theme: 'legacy' });

// Resolved once: both the prod-mode decision below and provideEnvironmentConfig() need it, and two
// independent lookups could drift. `null` = hostname not mapped; that is raised inside the app
// initializer, not here (see determineConfigEnvironment).
const cloudEnv: RcEnvironment | null =
  environment.deploymentTarget === 'cloud' ? determineConfigEnvironment(getHostname()) : null;

if (environment.deploymentTarget === 'onprem') {
  if (environment.ENV === 'server') {
    enableProdMode();
  }
} else if (cloudEnv !== 'local') {
  enableProdMode();
}

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-061681}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___' +
    '{Dr._Ing._h.c._F._Porsche_AG}_is_granted_a_{Multiple_Applications}_Developer_License_for_{3}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___' +
    'This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{5_August_2027}____[v3]_[01]_MTgxNzQyMDQwMDAwMA==93487b2c22b3a470751622bf1fd6a129',
);

registerLocaleData(localeEN); // TODO: check if still needed
registerLocaleData(localeDE);

moment.locale('de');

export class CustomTranslateLoader implements TranslateLoader {
  public getTranslation(lang: string): Observable<TranslationObject> {
    const translations: Record<string, Record<string, string>> = { en: enTranslations, de: deTranslations };
    return of(translations[lang] || enTranslations);
  }
}

export function loaderFactory(): TranslateLoader {
  return new CustomTranslateLoader();
}

bootstrapApplication(AppComponent, {
  providers: [
    ...(environment.deploymentTarget === 'cloud' ? [provideEnvironmentConfig(cloudEnv)] : []),
    provideAnimations(),
    provideNgxWebstorage(
      withNgxWebstorageConfig({ prefix: 'app', separator: ':' }),
      withLocalStorage(),
      withSessionStorage(),
    ),
    AppInitializers,
    provideAppInitializer(() => {
      initializeApiConfigFactory();
    }),
    provideAppInitializer(async () => {
      const appInitializers = inject(AppInitializers);
      return appInitializers.logInOnAppInit();
    }),
    provideAppInitializer(async () => appInitializerFactory()),
    LoginService,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    PercentPipe,
    { provide: ContextService, useClass: ContextServiceImpl, deps: [] },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true, deps: [ContextService] },
    { provide: LoggerService, useClass: ConsoleLoggerService },
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: loaderFactory,
      },
    }),
    provideRouter(APP_ROUTES, withHashLocation()),
  ],
});
